import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

// In dev (vite, port 5174) we hit the API at http://127.0.0.1:8005.
// In prod the SPA is served from https://admin.jusquant.ai and the
// FastAPI backend lives behind nginx at /api on the same origin.
const DEV_BASE = 'http://127.0.0.1:8005';
const baseURL = import.meta.env.DEV ? DEV_BASE : '/api';

const ACCESS_KEY = 'jq_admin_access';
const REFRESH_KEY = 'jq_admin_refresh';
const USER_KEY = 'jq_admin_user';

export interface StoredUser {
  email: string;
  totp_enrolled: boolean;
}

export function getAccessToken(): string | null {
  try {
    return window.localStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function getStoredUser(): StoredUser | null {
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

export function setTokens(access: string, refresh: string, user: StoredUser): void {
  window.localStorage.setItem(ACCESS_KEY, access);
  window.localStorage.setItem(REFRESH_KEY, refresh);
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearTokens(): void {
  window.localStorage.removeItem(ACCESS_KEY);
  window.localStorage.removeItem(REFRESH_KEY);
  window.localStorage.removeItem(USER_KEY);
}

let _client: AxiosInstance | null = null;

// [02/07] SINGLE-FLIGHT refresh: quando o access token expira, várias chamadas concorrentes
// levam 401 juntas. Cada uma tentava POST /auth/refresh com o MESMO refresh token (single-use,
// revogado no 1º uso) → a 2ª falhava → logout forçado várias vezes por dia. Agora todas as
// concorrentes compartilham UMA promise de refresh e re-tentam com o token novo.
let _refreshInFlight: Promise<string> | null = null;

function refreshOnce(): Promise<string> {
  if (_refreshInFlight === null) {
    const refresh = getRefreshToken();
    _refreshInFlight = axios
      .post(`${baseURL}/auth/refresh`, { refresh_token: refresh })
      .then((r) => {
        setTokens(r.data.access_token, r.data.refresh_token, r.data.user);
        return r.data.access_token as string;
      })
      .finally(() => {
        _refreshInFlight = null;
      });
  }
  return _refreshInFlight;
}

function buildClient(): AxiosInstance {
  const c = axios.create({ baseURL, timeout: 30_000 });
  c.interceptors.request.use((cfg) => {
    const token = getAccessToken();
    if (token) {
      cfg.headers = cfg.headers ?? {};
      (cfg.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return cfg;
  });
  c.interceptors.response.use(
    (r) => r,
    async (err: AxiosError) => {
      // 401 with a stored refresh token → try one rotation, retry the
      // original request once. Anything else propagates.
      const original = err.config as
        | (AxiosRequestConfig & { _retried?: boolean })
        | undefined;
      const status = err.response?.status;
      const refresh = getRefreshToken();
      if (status === 401 && refresh && original && !original._retried) {
        try {
          original._retried = true;
          const access = await refreshOnce();
          original.headers = original.headers ?? {};
          (original.headers as Record<string, string>).Authorization =
            `Bearer ${access}`;
          return c.request(original);
        } catch {
          clearTokens();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      }
      return Promise.reject(err);
    },
  );
  return c;
}

export function api(): AxiosInstance {
  if (_client === null) _client = buildClient();
  return _client;
}

export function getRetencao() {
  return api().get('/retencao');
}

export function getRetencaoEmail() {
  return api().get('/retencao/email');
}

// Curva de retorno download→upload (retenção ancorada no valor)
export function getRetencaoCurvaRetorno(params?: {
  unidade?: 'semana' | 'dia';
  modo?: 'cumulativo' | 'periodico';
  denominador?: 'maduro' | 'fixo';
}) {
  return api().get('/retencao/curva-retorno', { params });
}

// Cohorts mensais de signup — retenção comparada entre safras
export function getRetencaoCohortsMensais(params?: {
  modo?: 'periodico' | 'cumulativo';
  semanas?: number;
  segmento?: 'todos' | 'pagantes' | 'gratis';
}) {
  return api().get('/retencao/cohorts-mensais', { params });
}

// Recompra — retenção monetária (2ª compra Stripe desde a 1ª)
export function getRetencaoRecompra(params?: { janela_horas?: number }) {
  return api().get('/retencao/recompra', { params });
}

// Atividade — comportamento de usuários (routers/atividade.py)
export function getAtividade(days = 30) {
  return api().get('/atividade', { params: { days } });
}

// Impugnações — monitor do beta do subproduto Impugna (routers/impugnacoes.py)
export function getImpugnacoes(days = 30) {
  return api().get('/impugnacoes', { params: { days } });
}

// Impugnações — lista por-caso (drill-in de qualidade) e detalhe de uma
export function getImpugnaCasos() {
  return api().get('/impugnacoes/casos');
}

export function getImpugnaCasoDetalhe(impId: number) {
  return api().get(`/impugnacoes/casos/${impId}`);
}

// Unit Economics — CAC/LTV/payback/ROAS por canal (routers/unit_economics.py)
export function getUnitEconomics(days = 30) {
  return api().get('/unit-economics', { params: { days } });
}

// Qualidade (VA vs perito) — tendência do juiz-régua (routers/qualidade_va.py)
export function getQualidadeVA(dias = 21) {
  return api().get('/qualidade-va', { params: { dias } });
}

// Lotes (Cálculos em Massa) — saúde da função de lote (routers/lotes.py)
export function getLotesAdmin(dias = 28) {
  return api().get('/lotes-admin', { params: { dias } });
}

// VA por verba — perfil de defeito POR VERBA do juiz-régua (routers/va_por_verba.py)
export function getVAPorVerba(dias = 14) {
  return api().get('/va-por-verba', { params: { dias } });
}

// Golden5 vs Perito — placar da régua full-pipeline (routers/golden5.py)
export function getGolden5() {
  return api().get('/golden5');
}

// [FICHA A/B 28/09] Leitura do experimento da ficha de liquidação (routers/ficha_ab.py).
// Só leitura, e sem juízo de "bom/ruim": a aba mostra valor, diferença, IC95 e p.
// [METRICAS 08/10] O payload passou a vir em BLOCOS (elegíveis primário × ITT diluído), com
// coluna CUPED ("ajustado"), q-value de Benjamini-Hochberg, expected loss bayesiano e funil de
// exposição. O backend é quem decide os rótulos; a aba só renderiza o que vem.
export interface FichaAbPeriodo {
  desde?: string | null;
  ate?: string | null;
}

export function getFichaAbMetrics(periodo?: FichaAbPeriodo) {
  return api().get('/ficha-ab/metrics', {
    params: { desde: periodo?.desde || undefined, ate: periodo?.ate || undefined },
  });
}

/** Mesmo endpoint com `?csv=1` — devolve `por_org` como blob p/ download. */
export function getFichaAbCsv(periodo?: FichaAbPeriodo) {
  return api().get('/ficha-ab/metrics', {
    params: { desde: periodo?.desde || undefined, ate: periodo?.ate || undefined, csv: 1 },
    responseType: 'blob',
  });
}

export function getSatisfacao() {
  return api().get('/satisfacao');
}

// Índice diário de satisfação (0-100) — routers/satisfacao.py::indice_satisfacao
export function getSatisfacaoIndice(days = 14) {
  return api().get('/satisfacao/indice', { params: { days } });
}

export function getBayes(params?: any) {
  return api().get('/bayes', { params });
}

export function getBayesCaso(pid: number) {
  return api().get(`/bayes/${pid}`);
}

// [PJC_COBRADO 21/09] painel do A/B .pjc cobrado à parte (routers/pjc_ab.py)
export function getPjcAb() {
  return api().get('/pjc-ab');
}

// [MAX_AB 11/10] painel do A/B "Max" — premium de 3 créditos (routers/max_ab.py)
export function getMaxAb() {
  return api().get('/max-ab');
}

export function getMaxHealth(days = 30) {
  return api().get('/max-health', { params: { days } });
}

// [CNJ-WATCH 21/09] acompanhamento de processo — casos vigiados + reportes (routers/acompanhamento.py)
export function getAcompanhamento(params?: { incluir_internos?: boolean; ativo?: string; origem?: string; q?: string; limit?: number }) {
  return api().get('/acompanhamento', { params });
}

export function getJuiz(params?: any) {
  return api().get('/juiz', { params });
}

export function getJuizPiso() {
  return api().get('/juiz/piso');
}

// [CONCIERGE ADMIN 15/09] monitoramento do Concierge de Ajustes
export function getConciergeResumo(params?: any) {
  return api().get('/concierge/resumo', { params });
}
export function getConciergeSerie(params?: any) {
  return api().get('/concierge/serie', { params });
}
export function getConciergePedidos(params?: any) {
  return api().get('/concierge/pedidos', { params });
}
export function getConciergeDefeitos(params?: any) {
  return api().get('/concierge/defeitos', { params });
}
export function getConciergeForaEscopo(params?: any) {
  return api().get('/concierge/fora_escopo', { params });
}
// [INTERAÇÕES 21/09] uma linha por conversa de cliente com o Concierge: Pediu / Aceitou / Entregou / Avaliação
export function getConciergeInteracoes(params?: any) {
  return api().get('/concierge/interacoes', { params });
}
export function getChatInteraction(id: string) {
  return api().get(`/chat/interactions/${encodeURIComponent(id)}`);
}

export function getEdicao(params?: any) {
  return api().get('/edicao', { params });
}

/** Efetividade da edição pela Ficha de liquidação. Janela pela data da AÇÃO (`dias`),
 *  diferente do resto da aba — ver admin-api/routers/edicao_ficha.py. */
export function getEdicaoEfetividade(dias = 30) {
  return api().get('/edicao/efetividade', { params: { dias } });
}

export function getMelhoria() {
  return api().get('/melhoria');
}

// ── Loop de erro / dossiês do modelo híbrido ───────────────────────────────
// Espelha admin-api/routers/loop_erro.py. Toda seção pode voltar como
// [{ erro: '...' }] quando a tabela de origem ainda não existe.

/** Estados do contrato (erro_dossie.estado), na ordem do fluxo. */
export const ESTADOS_DOSSIE = [
  'novo',
  'em_sessao',
  'nao_e_nosso',
  'fix_gated',
  'pronto_flip',
  'flipado',
  'remediado',
  'descartado',
] as const;
export type EstadoDossie = (typeof ESTADOS_DOSSIE)[number];

export interface Dossie {
  id: number;
  issue_id?: number | null;
  assinatura?: string | null;
  processo_principal?: number | string | null;
  processo_ids?: Array<number | string> | null;
  org_ids?: Array<number | string> | null;
  estado?: EstadoDossie | string | null;
  erro_at?: string | null;
  dossie_at?: string | null;
  sessao_at?: string | null;
  fix_at?: string | null;
  flip_at?: string | null;
  flag?: string | null;
  lane?: string | null;
  path?: string | null;
  erro?: string;
}

/** O que `monitoring.dossie_erro.metricas()` devolve. Shape defensivo: o
 *  módulo é de fora do admin-api e pode mudar — o painel não quebra por isso. */
export interface MetricasDossie {
  gerado_em?: string;
  dias?: number;
  n_dossies?: number;
  /** médias por etapa em horas: h_erro_dossie, h_dossie_sessao, h_sessao_fix,
   *  h_fix_flip, h_flip_remediado */
  medias_horas?: Record<string, number | null> | null;
  por_estado?: Record<string, number> | null;
  /** uma linha por assinatura: dossies, flipados, flip_at, recorrencia_pos_flip */
  por_classe?: Array<Record<string, unknown>> | Record<string, unknown> | null;
  /** nome alternativo aceito pelo painel se o módulo renomear medias_horas */
  por_dossie?: Record<string, number | null> | null;
  [k: string]: unknown;
}

export interface LoopErroResp {
  issues?: Array<Record<string, unknown>>;
  funil?: Array<Record<string, unknown>>;
  erros_por_classe?: Array<Record<string, unknown>>;
  aviso_ao_cliente?: Array<Record<string, unknown>>;
  dossies?: Dossie[];
  metricas_dossie?: MetricasDossie;
  estados_dossie?: string[];
  /** presente quando erro_dossie ou monitoring.dossie_erro não respondeu */
  aviso?: string;
}

/** Painel do loop de fix de erro (admin-api/routers/loop_erro.py).
 *  Devolve: issues, funil, erros_por_classe, aviso_ao_cliente, dossies,
 *  metricas_dossie, estados_dossie e (quando algo falta) aviso. */
export function getLoopErro() {
  return api().get<LoopErroResp>('/loop-erro');
}

/** Markdown do dossiê (só leitura; o path é validado no backend). */
export function getDossie(id: number) {
  return api().get<{
    ok: boolean;
    id?: number;
    assinatura?: string | null;
    path?: string;
    markdown?: string;
    erro?: string;
  }>(`/loop-erro/dossie/${id}`);
}

/** Anda o dossiê de estado; o backend carimba o timestamp da etapa. */
export function setDossieEstado(id: number, estado: string, notas?: string) {
  return api().post<{
    ok: boolean;
    carimbou?: string | null;
    dossie?: Dossie;
    erro?: string;
  }>(`/loop-erro/dossie/${id}/estado`, { estado, notas: notas ?? null });
}

export function getReferrals() {
  return api().get('/referrals');
}

export function getFeedback() {
  return api().get('/feedback');
}

export function getNewsletterKpis() {
  return api().get('/newsletter/kpis');
}

export function getNewsletterEdicoes() {
  return api().get('/newsletter/edicoes');
}

export function getNewsletterPosts() {
  return api().get('/newsletter/posts');
}

export function despublicarNewsletterPost(postId: number) {
  return api().post(`/newsletter/posts/${postId}/despublicar`);
}

// Prospector — cold outbound (routers/prospector.py)
export function getProspectorKpis() {
  return api().get('/prospector/kpis');
}

export function getProspectorLeads(dias = 0, limite = 500) {
  return api().get('/prospector/leads', { params: { dias, limite } });
}

export function getProspectorThreads(dias = 0, limite = 200) {
  return api().get('/prospector/threads', { params: { dias, limite } });
}

// Funil a–n (fase 1): 1 linha por campanha (ou segmento) com as 14 métricas.
export function getProspectorFunil(params: {
  dias?: number;
  segmento?: string;
  campanha?: string;
  granularidade?: 'campanha' | 'segmento';
}) {
  return api().get('/prospector/funil', { params });
}

// Lote do dia + fila de aprovação (só leitura; o GO continua no Telegram).
export function getProspectorLote() {
  return api().get('/prospector/lote');
}

// Atribuições lead→user/org persistidas (lead_user_match).
export function getProspectorMatches() {
  return api().get('/prospector/matches');
}

// Registra uma réplica mandada à mão do Zoho contato@ (idempotente).
export function postProspectorRespostaManual(body: {
  lead_id: number;
  enviado_em: string;
  assunto?: string;
  trecho?: string;
  from_email?: string;
}) {
  return api().post('/prospector/resposta-manual', body);
}

export function postProspectorMatch(
  matchId: number,
  status: 'confirmado' | 'descartado',
) {
  return api().post(`/prospector/match/${matchId}`, { status });
}

// 30 Clientes Top — maiores clientes da história por receita Stripe
// (routers/top_clientes.py)
export function getTopClientes(limit = 30) {
  return api().get('/top-clientes', { params: { limit } });
}

// Créditos & Receita (routers/creditos.py)
export function getCreditosOverview() {
  return api().get('/creditos/overview');
}

export function getCreditosOrg(orgId: number, page = 1, pageSize = 50) {
  return api().get(`/creditos/org/${orgId}`, {
    params: { page, page_size: pageSize },
  });
}

export function previewNewsletterEdicao(editionId: number) {
  return api().post(`/newsletter/edicoes/${editionId}/preview`);
}

export function buildQueryString(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function extractErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { detail?: string } } } | undefined;
  return axiosErr?.response?.data?.detail ?? fallback;
}
